<template>
    <button
        :type="type"
        :disabled="disabled"
        @click="handleClick"
        :class="[
            'app-btn',
            { 'border-none': !borderOnly },
            { 'border-only': borderOnly },
            { 'my-3 mt-4': !noMargin },
            { disabled },
            { condensed },
            variant ? `btn-${variant}` : 'btn-primary',
        ]"
    >
        <slot />
    </button>
</template>

<script>
export default {
    props: {
        noMargin: { type: Boolean, default: false },
        disableOnClick: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        type: { type: String, default: "submit" },
        borderOnly: { type: Boolean, default: false },
        condensed: { type: Boolean, default: false },
        // primary | secondary | success | danger | warning | info
        variant: { type: String, default: 'primary' },
    },
    methods: {
        handleClick(event) {
            if (this.disabled) {
                event.preventDefault()
                return
            }
            this.$emit("click", event)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";
@import "@/assets/mixins.scss";
.app-btn {
    font-size: 0.85em;
    color: white;
    padding: 10px;
    border-radius: 5px;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.3);
    min-width: 120px;
    background-color: $primaryColor;
    transition: 0.3s;
    &:hover:not(.disabled) {
        // background-color: lighten($primaryColor, 15%);
        // opacity: 0.7;
        background-color: darken($primaryColor, 3%);
    }
    &.disabled {
        opacity: 0.6;
    }
    &.condensed {
        padding: 8px;
        font-size: 0.8em;
    }
    &:focus-visible {
        @include focus-ring;
    }
}
.border-only {
    color: $primaryColor;
    background-color: $lightCardBg;
    border: 1px solid $primaryColor;
}
.border-only:hover:not(.disabled) {
    color: white;
    background-color: darken($primaryColor, 3%);
    border-color: darken($primaryColor, 3%);
}

/* Dark theme overrides via ancestor `.dark` */
:deep(.dark) .app-btn {
    background-color: $primaryColorDark;
    color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
:deep(.dark) .app-btn:hover:not(.disabled) {
    background-color: darken($primaryColorDark, 5%);
}

:deep(.dark) .border-only {
    color: $secondaryColorDark;
    background-color: transparent;
    border: 1px solid $secondaryColorDark;
}
:deep(.dark) .border-only:hover:not(.disabled) {
    border-color: lighten($secondaryColorDark, 5%);
    color: lighten($secondaryColorDark, 5%);
}

/* Variant helpers */
.btn-primary { background-color: $primaryColor; }
.btn-primary:hover:not(.disabled) { background-color: darken($primaryColor, 5%); }

.btn-secondary { background-color: $secondaryColor; }
.btn-secondary:hover:not(.disabled) { background-color: darken($secondaryColor, 5%); }

.btn-success { background-color: $successColor; }
.btn-success:hover:not(.disabled) { background-color: darken($successColor, 5%); }

.btn-danger { background-color: $dangerColor; }
.btn-danger:hover:not(.disabled) { background-color: darken($dangerColor, 5%); }

.btn-warning { background-color: $warningColor; color: #1f2937; }
.btn-warning:hover:not(.disabled) { background-color: darken($warningColor, 5%); }

.btn-info { background-color: $infoColor; }
.btn-info:hover:not(.disabled) { background-color: darken($infoColor, 5%); }

/* Border-only variants - ensure legibility on light surfaces */
.border-only.btn-primary { color: $primaryColor; border-color: $primaryColor; background-color: $lightCardBg; }
.border-only.btn-secondary { color: $secondaryColor; border-color: $secondaryColor; background-color: $lightCardBg; }
.border-only.btn-success { color: $successColor; border-color: $successColor; background-color: $lightCardBg; }
.border-only.btn-danger { color: $dangerColor; border-color: $dangerColor; background-color: $lightCardBg; }
.border-only.btn-warning { color: $warningColor; border-color: $warningColor; background-color: $lightCardBg; }
.border-only.btn-info { color: $infoColor; border-color: $infoColor; background-color: $lightCardBg; }

.border-only.btn-primary:hover:not(.disabled) { color: white; background-color: darken($primaryColor, 3%); border-color: darken($primaryColor, 3%); }
.border-only.btn-secondary:hover:not(.disabled) { color: white; background-color: darken($secondaryColor, 3%); border-color: darken($secondaryColor, 3%); }
.border-only.btn-success:hover:not(.disabled) { color: white; background-color: darken($successColor, 3%); border-color: darken($successColor, 3%); }
.border-only.btn-danger:hover:not(.disabled) { color: white; background-color: darken($dangerColor, 3%); border-color: darken($dangerColor, 3%); }
.border-only.btn-warning:hover:not(.disabled) { color: #1f2937; background-color: darken($warningColor, 3%); border-color: darken($warningColor, 3%); }
.border-only.btn-info:hover:not(.disabled) { color: white; background-color: darken($infoColor, 3%); border-color: darken($infoColor, 3%); }

:deep(.dark) .btn-primary { background-color: $primaryColorDark; }
:deep(.dark) .btn-primary:hover:not(.disabled) { background-color: darken($primaryColorDark, 5%); }

:deep(.dark) .btn-secondary { background-color: $secondaryColorDark; }
:deep(.dark) .btn-secondary:hover:not(.disabled) { background-color: darken($secondaryColorDark, 5%); }

:deep(.dark) .btn-success { background-color: $successColorDark; }
:deep(.dark) .btn-success:hover:not(.disabled) { background-color: darken($successColorDark, 5%); }

:deep(.dark) .btn-danger { background-color: $dangerColorDark; }
:deep(.dark) .btn-danger:hover:not(.disabled) { background-color: darken($dangerColorDark, 5%); }

:deep(.dark) .btn-warning { background-color: $warningColorDark; color: $darkTextColor; }
:deep(.dark) .btn-warning:hover:not(.disabled) { background-color: darken($warningColorDark, 5%); }

:deep(.dark) .btn-info { background-color: $infoColorDark; }
:deep(.dark) .btn-info:hover:not(.disabled) { background-color: darken($infoColorDark, 5%); }

/* In dark mode, keep border-only transparent to avoid low-contrast fills */
:deep(.dark) .border-only.btn-primary { color: $primaryColorDark; border-color: $primaryColorDark; background-color: transparent; }
:deep(.dark) .border-only.btn-secondary { color: $secondaryColorDark; border-color: $secondaryColorDark; background-color: transparent; }
:deep(.dark) .border-only.btn-success { color: $successColorDark; border-color: $successColorDark; background-color: transparent; }
:deep(.dark) .border-only.btn-danger { color: $dangerColorDark; border-color: $dangerColorDark; background-color: transparent; }
:deep(.dark) .border-only.btn-warning { color: $warningColorDark; border-color: $warningColorDark; background-color: transparent; }
:deep(.dark) .border-only.btn-info { color: $infoColorDark; border-color: $infoColorDark; background-color: transparent; }

:deep(.dark) .border-only.btn-primary:hover:not(.disabled) { color: white; background-color: darken($primaryColorDark, 5%); border-color: darken($primaryColorDark, 5%); }
:deep(.dark) .border-only.btn-secondary:hover:not(.disabled) { color: $darkTextColor; background-color: darken($secondaryColorDark, 5%); border-color: darken($secondaryColorDark, 5%); }
:deep(.dark) .border-only.btn-success:hover:not(.disabled) { color: white; background-color: darken($successColorDark, 5%); border-color: darken($successColorDark, 5%); }
:deep(.dark) .border-only.btn-danger:hover:not(.disabled) { color: white; background-color: darken($dangerColorDark, 5%); border-color: darken($dangerColorDark, 5%); }
:deep(.dark) .border-only.btn-warning:hover:not(.disabled) { color: $darkTextColor; background-color: darken($warningColorDark, 5%); border-color: darken($warningColorDark, 5%); }
:deep(.dark) .border-only.btn-info:hover:not(.disabled) { color: white; background-color: darken($infoColorDark, 5%); border-color: darken($infoColorDark, 5%); }
</style>
